import React, { useEffect } from 'react';
import axios from 'axios';
import { Container, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { addBreadcrumb, captureException, Severity } from '@sentry/react';
import { useMessage } from 'modules/intl';
import { browserHistory } from 'browser-history';
import { CollectorIntermediatePageSkeleton } from './CollectorIntermediatePageSkeleton';
import { InstallBanner } from './InstallBanner';
import {
  collectorBroadcast,
  useUninstallCollector,
  useCollectorItem
} from './hooks';
import useCollectorIntermediateStyles from './hooks/useCollectorIntermediateStyles';

const refreshDesktopCollectors = async (refreshURL: string) => {
  try {
    await axios.get(refreshURL);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { ecid } = error.response.headers;
      addBreadcrumb({
        type: 'http',
        category: 'refreshDesktopCollectors',
        data: { ecid: ecid, status: error.response.status },
        level: Severity.Info
      });
    }
    captureException(error);
  }
};

export const CollectorUninstallSuccessPage: React.FC = () => {
  const classes = useCollectorIntermediateStyles();
  const {
    collectorId,
    collectorItem,
    scope,
    loading,
    noAction,
    collectorMessage,
    redirectUrl
  } = useCollectorItem();
  const { uninstallCollector } = useUninstallCollector();
  const refreshURL = localStorage.getItem('collectorDesktopRefreshURL');

  const defaultUninstallSuccessMessage3 = useMessage(
    'collectors.uninstallSuccessMessage3'
  );

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    const saveStatus = async () => {
      if (collectorId && !noAction) {
        try {
          await uninstallCollector(collectorId, scope);
        } catch (e) {
          const msg = (e as Error).message;
          if (msg.includes('No collector found with id')) {
            console.info(msg);
          } else {
            console.error(e);
            throw e;
          }
        }
        if (refreshURL) {
          refreshDesktopCollectors(refreshURL);
          localStorage.removeItem('collectorDesktopRefreshURL');
        } else {
          await collectorBroadcast.postMessage(collectorId);
        }

        if (redirectUrl) {
          timeout = setTimeout(() => {
            browserHistory.replace('');
            window.location.assign(redirectUrl);
          }, 3000);
        }
      }
    };
    saveStatus();
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [
    collectorId,
    scope,
    uninstallCollector,
    refreshURL,
    noAction,
    redirectUrl
  ]);

  if (loading) return <CollectorIntermediatePageSkeleton />;
  return (
    <div
      className={classes.root}
      data-testid="collector-uninstall-success-page"
    >
      <Container className={classes.textContainer}>
        <Typography variant="h2">
          <FormattedMessage id="collectors.uninstallSuccessMessage1" />
        </Typography>
      </Container>
      <InstallBanner imageSrc={collectorItem?.imageSrc} classes={classes} />
      <Container className={classes.textContainer}>
        <Typography variant="h4">
          <FormattedMessage
            id="collectors.uninstallSuccessMessage2"
            values={{ collector: collectorItem?.name }}
          />
        </Typography>
        {collectorMessage && (
          <Typography
            data-testid="collector-message"
            variant="h4"
            textAlign="center"
          >
            {collectorMessage}
          </Typography>
        )}
        {collectorMessage && redirectUrl ? null : (
          <Typography variant="h4">
            <FormattedMessage
              id={`collectors.uninstallSuccessPage._${collectorId}.uninstallSuccessMessage3`}
              defaultMessage={defaultUninstallSuccessMessage3}
            />
          </Typography>
        )}
      </Container>
    </div>
  );
};

export default React.memo(CollectorUninstallSuccessPage);
